import { useState, useEffect, useCallback } from 'react';
import {
  Monitor, RefreshCw, Search, LogOut, Clock, Wifi,
  ShieldCheck, AlertTriangle, User, Loader2,
} from 'lucide-react';

import { sessionApi } from '@/api/session.api';
import type { UserSessionDto } from '@/api/session.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { Spinner }       from '@/components/ui/Spinner';
import { cn }            from '@/utils/cn';

// ── Helpers ────────────────────────────────────────────────────────────────────

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    dateStyle: 'medium', timeStyle: 'short',
  });
};

const timeAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1)   return 'just now';
  if (minutes < 60)  return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)    return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

// ── Terminate confirm dialog ───────────────────────────────────────────────────

interface TerminateDialogProps {
  session: UserSessionDto;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

const TerminateDialog = ({ session, onConfirm, onCancel }: TerminateDialogProps) => {
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    await onConfirm();
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <LogOut size={18} className="text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Terminate Session</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">This action cannot be undone.</p>
            </div>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Are you sure you want to terminate the session for{' '}
            <strong>{session.fullName}</strong>? They will be signed out immediately.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={busy}
            className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy ? <><Loader2 size={14} className="animate-spin" /> Terminating…</> : 'Terminate'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Session row ────────────────────────────────────────────────────────────────

interface SessionRowProps {
  session: UserSessionDto;
  canTerminate: boolean;
  onTerminate: (s: UserSessionDto) => void;
}

const SessionRow = ({ session, canTerminate, onTerminate }: SessionRowProps) => {
  const initials = session.fullName
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

  return (
    <div className={cn(
      'flex items-center gap-4 px-4 py-3 rounded-xl border transition-colors',
      session.isCurrentSession
        ? 'border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20'
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-gray-800/50',
    )}>
      {/* Avatar */}
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
        <span className="text-xs font-bold text-indigo-700 dark:text-indigo-400">{initials}</span>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{session.fullName}</span>
          {session.isCurrentSession && (
            <span className="text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 px-2 py-0.5 rounded-full font-medium">
              This session
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
          <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <User size={10} />
            {session.username}
          </span>
          {session.ipAddress && (
            <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              <Wifi size={10} />
              {session.ipAddress}
            </span>
          )}
          <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <Clock size={10} />
            Signed in {timeAgo(session.loginDate)}
          </span>
        </div>
        {session.deviceInfo && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{session.deviceInfo}</p>
        )}
      </div>

      {/* Activity & expiry */}
      <div className="hidden sm:flex flex-col items-end gap-0.5 flex-shrink-0 min-w-32">
        <span className="text-xs text-gray-600 dark:text-gray-400">Active {timeAgo(session.lastActivityDate)}</span>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          Expires {formatDateTime(session.expiresAt)}
        </span>
      </div>

      {/* Terminate button */}
      {canTerminate && !session.isCurrentSession && (
        <button
          onClick={() => onTerminate(session)}
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-xs font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          <LogOut size={12} />
          Terminate
        </button>
      )}
    </div>
  );
};

// ── Page ───────────────────────────────────────────────────────────────────────

export const SessionManagementPage = () => {
  const { hasPermission } = usePermission();
  const canView      = hasPermission(Permissions.ADMIN_SESSION_VIEW);
  const canTerminate = hasPermission(Permissions.ADMIN_SESSION_TERMINATE);

  const [sessions,    setSessions]    = useState<UserSessionDto[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [terminating, setTerminating] = useState<UserSessionDto | null>(null);
  const [toast,       setToast]       = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await sessionApi.getList({ pageSize: 200 });
      setSessions(res.data.data?.items ?? []);
    } catch {
      showToast('error', 'Failed to load sessions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh the list on an interval so terminations and new logins show without a manual reload (QA #80).
  useEffect(() => {
    const id = setInterval(() => { load(); }, 15000);
    return () => clearInterval(id);
  }, [load]);

  const handleTerminate = async () => {
    if (!terminating) return;
    try {
      await sessionApi.terminate(terminating.id);
      showToast('success', `Session for ${terminating.fullName} terminated.`);
      setTerminating(null);
      await load();
    } catch {
      showToast('error', 'Failed to terminate session.');
      setTerminating(null);
    }
  };

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        <ShieldCheck size={20} className="mr-2" /> You do not have permission to view sessions.
      </div>
    );
  }

  const filtered = sessions.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.fullName.toLowerCase().includes(q) ||
      s.username.toLowerCase().includes(q) ||
      (s.ipAddress ?? '').includes(q)
    );
  });

  const activeCount  = sessions.length;
  const currentFirst = [...filtered].sort((a, b) =>
    (b.isCurrentSession ? 1 : 0) - (a.isCurrentSession ? 1 : 0),
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
            <Monitor size={20} className="text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Active Sessions</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {activeCount} active session{activeCount !== 1 ? 's' : ''} across the organisation
            </p>
          </div>
        </div>

        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50"
        >
          <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div className={cn(
          'flex items-center gap-2 rounded-lg px-4 py-3 text-sm',
          toast.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800',
        )}>
          {toast.type === 'success' ? <ShieldCheck size={16} /> : <AlertTriangle size={16} />}
          {toast.msg}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name, username, or IP address…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Session list */}
      {loading ? (
        <div className="flex items-center justify-center h-40"><Spinner /></div>
      ) : currentFirst.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 text-gray-500 dark:text-gray-400 gap-2">
          <Monitor size={32} className="text-gray-300" />
          <p className="text-sm">{search ? 'No sessions match your search.' : 'No active sessions found.'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {currentFirst.map(s => (
            <SessionRow
              key={s.id}
              session={s}
              canTerminate={canTerminate}
              onTerminate={setTerminating}
            />
          ))}
        </div>
      )}

      {/* Stats footer */}
      {!loading && sessions.length > 0 && (
        <div className="grid grid-cols-3 gap-4 pt-2">
          {[
            { label: 'Total Sessions', value: sessions.length },
            { label: 'Unique Users',   value: new Set(sessions.map(s => s.userId)).size },
            { label: 'Filtered',       value: filtered.length },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800 px-4 py-3 text-center">
              <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-400">{value}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Terminate dialog */}
      {terminating && (
        <TerminateDialog
          session={terminating}
          onConfirm={handleTerminate}
          onCancel={() => setTerminating(null)}
        />
      )}
    </div>
  );
};

export default SessionManagementPage;
